import { axiosInstance } from "./axiosInstance.js";
import {
  CROPYEAR_CREATOR,
  FIELD_CREATOR,
  FOUR_R_ASSESSMENT_CREATOR,
  FOUR_R_FERTILIZER_APPLICATION_CREATOR,
  FOUR_R_SOIL_TEST_CREATOR,
} from "../Assets/contextFactories_V0.js";

function unwrapPayload(payload) {
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }

  return payload;
}

function buildLegacyHeaders(user) {
  if (!user?.token) {
    return {
      "Content-Type": "application/json",
    };
  }

  return {
    token: `Bearer ${user.token}`,
    provider: user.provider || "microsoft",
    "Content-Type": "application/json",
  };
}

function normalizeFertilizerRate(rateData, blankRate) {
  return {
    ...blankRate,
    ...(rateData || {}),
    pre_seed: rateData?.pre_seed ?? rateData?.preSeed ?? blankRate.pre_seed,
    with_seed: rateData?.with_seed ?? rateData?.withSeed ?? blankRate.with_seed,
    post_seed: rateData?.post_seed ?? rateData?.postSeed ?? blankRate.post_seed,
  };
}

function normalizeCropYearShape(cropyearData) {
  const blank = CROPYEAR_CREATOR(cropyearData?.field_id);
  const fertilizerRates = cropyearData?.fertilizer_rates || {};
  const harvest = cropyearData?.harvest || {};

  return {
    ...blank,
    ...cropyearData,
    cultivations: Array.isArray(cropyearData?.cultivations)
      ? cropyearData.cultivations
      : blank.cultivations,
    fertilizer_applications: Array.isArray(cropyearData?.fertilizer_applications)
      ? cropyearData.fertilizer_applications
      : blank.fertilizer_applications,
    pesticide_applications: Array.isArray(cropyearData?.pesticide_applications)
      ? cropyearData.pesticide_applications
      : blank.pesticide_applications,
    planting_operation: {
      ...blank.planting_operation,
      ...(cropyearData?.planting_operation || {}),
    },
    fertilizer_rates: {
      ...blank.fertilizer_rates,
      ...fertilizerRates,
      N: normalizeFertilizerRate(fertilizerRates.N, blank.fertilizer_rates.N),
      P: normalizeFertilizerRate(fertilizerRates.P, blank.fertilizer_rates.P),
      K: normalizeFertilizerRate(fertilizerRates.K, blank.fertilizer_rates.K),
      S: normalizeFertilizerRate(fertilizerRates.S, blank.fertilizer_rates.S),
      M: normalizeFertilizerRate(fertilizerRates.M, blank.fertilizer_rates.M),
    },
    harvest: {
      ...blank.harvest,
      ...harvest,
      swather: {
        ...blank.harvest.swather,
        ...(harvest.swather || {}),
      },
      combine: {
        ...blank.harvest.combine,
        ...(harvest.combine || {}),
      },
      moisture: {
        ...blank.harvest.moisture,
        ...(harvest.moisture || {}),
      },
    },
  };
}

function unwrapSingleRecord(payload) {
  const data = unwrapPayload(payload);

  if (Array.isArray(data)) {
    return data[0] ?? null;
  }

  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    !("id" in data) &&
    !("public_id" in data) &&
    !("name" in data)
  ) {
    const values = Object.values(data);

    if (
      values.length === 1 &&
      values[0] &&
      typeof values[0] === "object" &&
      !Array.isArray(values[0])
    ) {
      return values[0];
    }
  }

  return data;
}

function normalizeFieldShape(fieldData) {
  const blank = FIELD_CREATOR();
  const rawField = unwrapSingleRecord(fieldData);

  if (!rawField || typeof rawField !== "object") {
    return blank;
  }

  const boundaries = Array.isArray(rawField.boundaries)
    ? rawField.boundaries
    : blank.boundaries;

  return {
    ...blank,
    ...rawField,
    field_size: rawField.field_size === 0 ? "" : rawField.field_size ?? blank.field_size,
    boundaries,
    has_boundaries: rawField.has_boundaries ?? Boolean(boundaries.length),
    field_boundaries_size:
      rawField.field_boundaries_size ?? blank.field_boundaries_size,
  };
}

function normalizeFourRAssessmentShape(assessmentData) {
  const blank = FOUR_R_ASSESSMENT_CREATOR();
  const rawAssessment = unwrapSingleRecord(assessmentData);

  if (!rawAssessment || typeof rawAssessment !== "object") {
    return blank;
  }

  return {
    ...blank,
    ...rawAssessment,
    soil_tests:
      Array.isArray(rawAssessment.soil_tests) && rawAssessment.soil_tests.length > 0
      ? rawAssessment.soil_tests.map((test) => ({
          ...FOUR_R_SOIL_TEST_CREATOR(),
          ...(test || {}),
          nutrient: test?.nutrient ?? "",
          test: test?.test ?? "",
          date: test?.date ?? "",
          result: test?.result ?? 0,
          units: test?.units ?? "",
          frequency: test?.frequency ?? "",
        }))
      : blank.soil_tests,
    fertilizer_applications:
      Array.isArray(rawAssessment.fertilizer_applications) &&
      rawAssessment.fertilizer_applications.length > 0
      ? rawAssessment.fertilizer_applications.map((application) => ({
          ...FOUR_R_FERTILIZER_APPLICATION_CREATOR(),
          ...(application || {}),
          application_date: application?.application_date ?? "",
          application_method: application?.application_method ?? "",
          fertilizer_timing: application?.fertilizer_timing ?? "",
          incorporation_depth:
            application?.incorporation_depth ??
            FOUR_R_FERTILIZER_APPLICATION_CREATOR().incorporation_depth,
          soil_state: application?.soil_state ?? "",
          fertilizer_mix_id: application?.fertilizer_mix_id ?? "",
        }))
      : blank.fertilizer_applications,
    operations_questionnaire: {
      ...blank.operations_questionnaire,
      ...(rawAssessment.operations_questionnaire || {}),
      N: {
        ...blank.operations_questionnaire.N,
        ...(rawAssessment.operations_questionnaire?.N || {}),
      },
      P: {
        ...blank.operations_questionnaire.P,
        ...(rawAssessment.operations_questionnaire?.P || {}),
      },
    },
    assessment_results: {
      ...blank.assessment_results,
      ...(rawAssessment.assessment_results || {}),
      rate_nitrogen: {
        ...blank.assessment_results.rate_nitrogen,
        ...(rawAssessment.assessment_results?.rate_nitrogen || {}),
      },
      rate_phos: {
        ...blank.assessment_results.rate_phos,
        ...(rawAssessment.assessment_results?.rate_phos || {}),
      },
      time_nitrogen: {
        ...blank.assessment_results.time_nitrogen,
        ...(rawAssessment.assessment_results?.time_nitrogen || {}),
      },
      time_phos: {
        ...blank.assessment_results.time_phos,
        ...(rawAssessment.assessment_results?.time_phos || {}),
      },
      placement_nitrogen: {
        ...blank.assessment_results.placement_nitrogen,
        ...(rawAssessment.assessment_results?.placement_nitrogen || {}),
      },
      placement_phos: {
        ...blank.assessment_results.placement_phos,
        ...(rawAssessment.assessment_results?.placement_phos || {}),
      },
    },
  };
}

async function fetchFieldBoundariesSummary(user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/me/field-boundaries`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return unwrapPayload(response.data) || [];
}

async function fetchDashboardSummary(user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/dashboard/summary`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return unwrapPayload(response.data) || {};
}

async function fetchDashboardNavigation(user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/dashboard/navigation`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return unwrapPayload(response.data) || {};
}

async function validateFieldBoundaries(boundaryRequest, user) {
  const response = await axiosInstance.post(
    `${process.env.REACT_APP_API_URL}/fields/validate-boundaries`,
    boundaryRequest,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return unwrapPayload(response.data);
}

async function resolveFieldSoil({ lat, lng, province }, user) {
  const params = {
    lat,
    lng,
  };

  if (province) {
    params.province = province;
  }

  const response = await axiosInstance.get("/fields/resolve-soil", {
    params,
    headers: buildLegacyHeaders(user),
  });

  return unwrapSingleRecord(response.data);
}

async function fetchFieldSoilEnrichment(farmId, fieldId, user) {
  const response = await axiosInstance.get(
    `/farms/${farmId}/fields/${fieldId}/soil-enrichment`,
    { headers: buildLegacyHeaders(user) }
  );

  return unwrapPayload(response.data) || response.data;
}

async function refreshFieldSoilEnrichment(farmId, fieldId, user) {
  const response = await axiosInstance.post(
    `/farms/${farmId}/fields/${fieldId}/soil-enrichment/refresh`,
    {},
    { headers: buildLegacyHeaders(user) }
  );

  return unwrapSingleRecord(response.data);
}

async function GetSetFarm(farmId, user, farmContext) {
  const farm = await fetchFarm(farmId, user);
  farmContext.setter(farm);
}

async function GetSetField(farmId, fieldId, user, fieldContext) {
  fieldContext.setter(await fetchField(farmId, fieldId, user));
}

async function GetSetFieldWithCropYears(farmId, fieldId, user, fieldContext) {
  const fieldResponse = await axiosInstance.get(
    process.env.REACT_APP_API_URL + "/farms/" + farmId + "/fields/" + fieldId,
    {
      headers: buildLegacyHeaders(user),
    }
  );
  const cropyearsResponse = await axiosInstance.get(
    process.env.REACT_APP_API_URL +
      "/farms/" +
      farmId +
      "/fields/" +
      fieldId +
      "/cropyears",
    {
      headers: buildLegacyHeaders(user),
    }
  );
  fieldContext.setter({
    ...normalizeFieldShape(fieldResponse.data),
    crop_years: cropyearsResponse.data,
  });
}

async function GetSetCropYear(
  farmId,
  fieldId,
  cropyearId,
  user,
  cropYearContext
) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/${cropyearId}`,
    {
      headers: buildLegacyHeaders(user),
    }
  );
  cropYearContext.setter(normalizeCropYearShape(response.data));
}

async function GetSetAnalysis(cropyearId, user, reportDataConext) {
  reportDataConext.setter(await fetchAnalysis(cropyearId, user));
}

async function fetchFarm(farmId, user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/farms/${farmId}`,
    {
      headers: buildLegacyHeaders(user),
    }
  );
  const machinesResponse = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/machines`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return {
    ...unwrapPayload(response.data),
    machines: unwrapPayload(machinesResponse.data).filter((machine) => machine.farm_id == null || Number(machine.farm_id) === Number(farmId)),
  };
}

async function fetchField(farmId, fieldId, user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return normalizeFieldShape(response.data);
}

async function fetchCropYear(farmId, fieldId, cropyearId, user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/farms/${farmId}/fields/${fieldId}/cropyears/${cropyearId}`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return normalizeCropYearShape(response.data);
}

async function fetchFourRAssessment(cropyearId, user) {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_URL}/assessments/${cropyearId}`,
      {
        headers: buildLegacyHeaders(user),
      }
    );

    if (response.data == null || response.data === false) {
      return null;
    }

    return normalizeFourRAssessmentShape(response.data);
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }

    throw error;
  }
}

async function fetchAnalysis(cropyearId, user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/cropyears/${cropyearId}/analysis`,
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return response.data;
}

// Returns null when no Holos job exists yet for this crop year (404) -- that is
// a valid "not started" state, not an error, so callers should not need a try/catch.
async function fetchHolosJob(cropyearId, user) {
  try {
    const response = await axiosInstance.get(
      `${process.env.REACT_APP_API_URL}/cropyears/${cropyearId}/holos-job`,
      {
        headers: buildLegacyHeaders(user),
      }
    );

    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      return null;
    }

    throw error;
  }
}

// Enqueues a Holos GHG calculation for this crop year. Idempotent while a job is
// PENDING/SENDING -- the backend returns the existing job rather than double-submitting.
async function postHolosJob(cropyearId, user) {
  const response = await axiosInstance.post(
    `${process.env.REACT_APP_API_URL}/cropyears/${cropyearId}/holos-job`,
    {},
    {
      headers: buildLegacyHeaders(user),
    }
  );

  return response.data;
}

async function fetchHolosHistory(cropyearId, user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/cropyears/${cropyearId}/holos-job/history`,
    { headers: buildLegacyHeaders(user) }
  );
  return unwrapPayload(response.data) || [];
}

async function fetchAnalysisHistory(cropyearId, user) {
  const response = await axiosInstance.get(
    `${process.env.REACT_APP_API_URL}/cropyears/${cropyearId}/analysis/history`,
    { headers: buildLegacyHeaders(user) }
  );
  return unwrapPayload(response.data) || [];
}

export {
  buildLegacyHeaders,
  fetchAnalysis,
  fetchHolosJob,
  fetchHolosHistory,
  fetchAnalysisHistory,
  postHolosJob,
  fetchDashboardNavigation,
  fetchDashboardSummary,
  fetchFieldBoundariesSummary,
  fetchCropYear,
  fetchFarm,
  fetchField,
  fetchFieldSoilEnrichment,
  fetchFourRAssessment,
  resolveFieldSoil,
  refreshFieldSoilEnrichment,
  validateFieldBoundaries,
  normalizeFieldShape,
  normalizeCropYearShape,
  normalizeFourRAssessmentShape,
  GetSetFarm,
  GetSetField,
  GetSetFieldWithCropYears,
  GetSetCropYear,
  GetSetAnalysis,
  axiosInstance,
};
